<template>
  <div
    id="display-properties-sidebar"
    class="h-100 bg-light border-left d-flex overflow-hidden"
  >
    <div
      id="icons"
      :class="['flex-shrink-0 d-flex flex-column align-items-center', {'border-right': isSidebarOpen}]"
    >
      <b-button
        id="sidebar-toggle-btn"
        variant="tertiary"
        class="tab-icon d-flex align-items-center justify-content-center"
        @click="toggleSidebarOpen('')"
        @mouseover="setTooltipTarget('sidebar-toggle-btn')"
        @mouseleave="setTooltipTarget('')"
        :disabled="!canToggleSidebar"
        data-test="sidebar-toggle-btn"
      >
        <i :class="`icon fal ${isSidebarOpen ? 'fa-chevron-double-right' : 'fa-chevron-double-left'}`" />
      </b-button>
      <div
        v-for="(section, index) in getTabs"
        :key="index"
        class="w-100"
      >
        <section
          v-if="section.length > 0"
          class="d-flex flex-column align-items-center"
        >
          <b-button
            v-for="(tab) in section"
            :key="tab.id"
            :id="tab.id"
            variant="tertiary"
            :class="['tab-icon d-flex align-items-center justify-content-center', {'tab-active' : getActiveTab === tab.id}]"
            @click="toggleSidebarOpen(tab.id)"
            @mouseover="setTooltipTarget(tab.id)"
            @mouseleave="setTooltipTarget('')"
            :disabled="!canToggleSidebar"
            :data-test="tab.id"
          >
            <i
              :class="[`icon ${getTabInfo(tab.id, 'icon')}`, {'tab-active' : getActiveTab === tab.id}]"
            />
          </b-button>
        </section>
        <hr
          v-if="getTabs.length > 1 && section.length > 0 && index !== getTabs.length -1"
          class="my-2"
        >
      </div>
    </div>
    <div
      id="display-properties-content-container"
      ref="display-properties-content-container"
      class="bg-white flex-grow-1"
      :class="{'is-open': isSidebarOpen}"
      data-test="display-properties-content-container"
    >
      <div
        id="display-properties-content"
        class="h-100 p-4"
        :data-test="`${getActiveTab}-tab`"
      >
        <component
          :is="getTabInfo(getActiveTab, 'component')"
          :key="getTabInfo(getActiveTab, 'component')"
          :is-sidebar-open="isSidebarOpen"
        />
      </div>
    </div>
    <template
      v-if="tooltip.target !== '' && !tooltip.isDisabled"
    >
      <b-tooltip
        :key="tooltip.target"
        :target="tooltip.target"
        triggers="hover"
        placement="left"
      >
        <template
          v-if="tooltip.target === 'sidebar-toggle-btn'"
        >
          {{ `${!isSidebarOpen ? 'Open' : 'Close'} Sidebar` }}
        </template>
        <template
          v-else
        >
          {{ getTabInfo(tooltip.target, 'tooltip') }}
        </template>
      </b-tooltip>
    </template>
  </div>
</template>
<script>
import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';

import MasterCreativeProperties from '@/modules/content/components/CreativesPage/Sidebar/MasterCreativeProperties';
import UnitCreativeProperties from '@/modules/content/components/CreativesPage/Sidebar/UnitCreativeProperties';
import DisplayContent from '@/modules/content/components/CreativesPage/Sidebar/DisplayContent';
import MediaAssets from '@/modules/content/components/CreativesPage/Sidebar/MediaAssets/MediaAssets';
import CustomProperties from '@/modules/content/components/CreativesPage/Sidebar/CustomProperties';
import TrackersProperties from '@/modules/content/components/CreativesPage/Sidebar/SSTrackersProperties';
import Comments from '@/modules/content/components/CreativesPage/Sidebar/Comments';
import CreativeType from '@/modules/content/components/CreativesPage/Sidebar/CreativeType/CreativeType';

export default {
  name: 'DisplayPropertiesSidebar',
  components: {
    CreativeType,
    MasterCreativeProperties,
    UnitCreativeProperties,
    DisplayContent,
    MediaAssets,
    CustomProperties,
    TrackersProperties,
    Comments,
  },
  props: {
    isSidebarOpen: {
      type: Boolean,
      default: false,
    },
    selectedTabId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      activeTabId: '',
      transitionTimer: 0.5,
      tooltip: {
        isDisabled: false,
        target: '',
      },
    };
  },
  computed: {
    ...mapState('content', [
      'isAddingNewAnnotation',
      'selectedArticleUuid',
      'activeCreatives',
      'creativeTypeSelected',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_MANAGED_CAMPAIGN,
      campaignTypes.GET_HAS_CAMPAIGN_ACCESS,
      campaignTypes.GET_IS_SELF_SERVICE_CAMPAIGN_ACCESSED_BY_ADMIN,
    ]),
    ...mapGetters('content', [
      contentTypes.GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS,
    ]),
    getActiveTab() {
      if (this.selectedTabId) return this.selectedTabId;
      return this.activeTabId ? this.activeTabId : this.getTabs[0][0].id;
    },
    getCreativeTypeTabs() {
      return [
        {
          id: 'creative-type',
          icon: 'fa-kit fa-light-rectangles-mixed-rotate',
          tooltip: 'Creative Type',
          component: 'CreativeType',
        },
      ];
    },
    getCreativeCustomisableTabs() {
      if (this.creativeTypeSelected !== 'CUSTOMISABLE') return [];
      return [
        {
          id: 'master-creative-properties',
          icon: 'master-creative-properties',
          tooltip: 'Master Creative Properties',
          component: 'MasterCreativeProperties',
        },
        {
          id: 'unit-creative-properties',
          icon: 'fal fa-paintbrush-pencil',
          tooltip: 'Unit Creative Properties',
          component: 'UnitCreativeProperties',
        },
      ];
    },
    getDisplayPropertiesTabs() {
      const tabs = [{
        id: 'display-properties-image',
        icon: 'fal fa-photo-film',
        tooltip: 'Media Assets',
        component: 'MediaAssets',
      }];
      if (this.creativeTypeSelected !== 'IMAGE') {
        tabs.unshift(
          {
            id: 'display-properties-text',
            icon: 'fal fa-text',
            tooltip: 'Display Properties Text',
            component: 'DisplayContent',
          },
        );
      }
      if (this.GET_IS_MANAGED_CAMPAIGN) {
        tabs.push({
          id: 'custom-properties',
          icon: 'fal fa-brackets-curly',
          tooltip: 'Custom Properties',
          component: 'CustomProperties',
        });
      } else {
        tabs.push({
          id: 'creative-trackers',
          icon: 'fal fa-crosshairs-simple',
          tooltip: 'Trackers',
          component: 'TrackersProperties',
        });
      }
      return tabs;
    },
    getCommentsManagementTabs() {
      return [
        {
          id: 'creative-template-comments',
          icon: 'fal fa-comments-alt',
          tooltip: 'Comments',
          component: 'Comments',
        },
      ];
    },
    getTabs() {
      if (!this.GET_HAS_CAMPAIGN_ACCESS && !this.GET_IS_SELF_SERVICE_CAMPAIGN_ACCESSED_BY_ADMIN) {
        return [
          this.getCommentsManagementTabs,
        ];
      }
      return [
        this.getCreativeTypeTabs,
        this.getCreativeCustomisableTabs,
        this.getDisplayPropertiesTabs,
        this.getCommentsManagementTabs,
      ].filter((tabs) => tabs.length > 0);
    },
    hasArticles() {
      return this.GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS.length > 0;
    },
    canToggleSidebar() {
      return this.hasArticles
        && this.selectedArticleUuid;
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.REMOVE_CURRENT_ANNOTATION,
    ]),
    ...mapMutations('content', [
      contentTypes.TOGGLE_ANNOTATIONS,
    ]),
    getTabInfo(tabId, info) {
      if (!tabId) return;
      let allTabs = [];
      this.getTabs.forEach((tabs) => {
        allTabs = allTabs.concat(tabs);
      });
      return allTabs.find((tab) => tab.id === tabId)[info];
    },
    async toggleSidebarOpen(activeTabId) {
      if (!activeTabId) {
        this.$emit('setIsSidebarOpen', !this.isSidebarOpen);
        return;
      }
      this.tooltip.isDisabled = true;
      if (this.isSidebarOpen && this.getActiveTab !== activeTabId) {
        this.$emit('setIsSidebarOpen', true);
        if (this.getTabInfo(this.getActiveTab, 'component') === 'Comments' && this.isAddingNewAnnotation) {
          this.TOGGLE_ANNOTATIONS(false);
          this.REMOVE_CURRENT_ANNOTATION();
        }
      } else this.$emit('setIsSidebarOpen', !this.isSidebarOpen);
      this.activeTabId = activeTabId;
      setTimeout(() => {
        this.tooltip.isDisabled = false;
      }, this.transitionTimer * 1000);
    },
    setTooltipTarget(targetId) {
      this.$set(this.tooltip, 'target', targetId);
    },
  },
  async mounted() {
    const displayPropertiesContent = this.$refs['display-properties-content-container'];
    if (!displayPropertiesContent) await this.$nextTick();
    displayPropertiesContent.style.setProperty('transition', `width ${this.transitionTimer}s`);
  },
};
</script>
<style lang="scss">
.master-creative-properties {
  background-image: url(/icons/paintbrush-pencil-globe.svg);
  background-repeat: no-repeat;
  width: 1.50rem;
  height: 1.5rem;
  &:hover {
    background-image: url(/icons/paintbrush-pencil-globe-link-hover.svg);
  }
  &.tab-active {
    background-image: url(/icons/paintbrush-pencil-globe-link-active.svg);
  }
}
</style>
<style scoped lang="scss">
$display-properties-content-width: 330px;
#display-properties-content-container {
  width: 0;
  &.is-open {
    width: $display-properties-content-width;
  }
}
#display-properties-content {
  overflow-y: auto;
  width: $display-properties-content-width;
}
#icons {
  width: 50px !important;
}
.tab-icon {
  border: unset;
  padding: 0.50rem;
  margin: 0.25rem;
  cursor: pointer;
  &:focus {
    border: unset;
    outline: none;
    box-shadow: none;
  }
  &:hover:not(.disabled) {
    background-color: $primary-light;
    color: $link-dark;
  }
  &.tab-active {
    color: $link;
  }
}
.icon {
  color: inherit;
  font-size: 1.25rem;
}
hr {
  padding: 0;
  width: 100%;
  height: 1px;
}
</style>
